import { useState, useEffect, useMemo, useRef } from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { FORM_MODE } from "../config/constants";
import EmptyState from "../components/Shared/EmptyState";
import Modal from "../components/Shared/Modal";
import ServiceForm from "../components/Service/ServiceForm";
import ServiceDataTable from "../components/Service/ServiceDataTable";
import ServiceAttentionStrip from "../components/Service/ServiceAttentionStrip";
import AlertModal from "../components/Shared/AlertModal";
import ServicePageSkeleton from "../components/Shared/LoadingSkeletons/ServicePageSkeleton";
import PrimaryButton from "../components/Shared/PrimaryButton";
import {
  getServices,
  saveService,
  patchService,
  deleteService,
} from "../api/serviceApi";
import { getAvailabilities } from "../api/availabilityApi";
import { deriveUnbookableServices } from "../utility/serviceBookability";
import { useToast } from "../hooks/useToast";
import { useSetupNavigation } from "../hooks/useSetupNavigation";
import useCreateIntent from "../hooks/useCreateIntent";

const ServicePage = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [defaultService, setDefaultService] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [serviceModalTitle, setServiceModalTitle] = useState(null);
  const modalCloseRef = useRef(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { handleSetupStepComplete, isSetupComplete } = useSetupNavigation();

  // Mobile bottom-nav "+" deep-links here with ?new=service
  useCreateIntent("service", () => setShowDrawer(true));

  const { data: servicesData = [], isLoading: isLoadingGetServices } = useQuery(
    {
      queryKey: ["services"],
      queryFn: getServices,
      placeholderData: keepPreviousData,
    }
  );

  const services = useMemo(() => servicesData, [servicesData]);

  // Bookability check — a service whose delivery type has no matching
  // availability can't take bookings and gets the amber attention treatment.
  // Gated on isSuccess (not length) so zero-availability providers see it too.
  const { data: availabilities = [], isSuccess: availabilitiesLoaded } =
    useQuery({
      queryKey: ["availabilities"],
      queryFn: getAvailabilities,
      staleTime: 5 * 60 * 1000,
    });

  const { bookabilityById, unbookable, providerTypes } = useMemo(
    () =>
      availabilitiesLoaded
        ? deriveUnbookableServices(services, availabilities)
        : { bookabilityById: {}, unbookable: [], providerTypes: {} },
    [availabilitiesLoaded, services, availabilities]
  );
  const { mutateAsync: mutateSaveService } = useMutation({
    mutationFn: saveService,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["services"], exact: true });
      closeDrawerHandler();

      // Handle setup flow navigation and feedback
      if (!isSetupComplete) {
        await handleSetupStepComplete(
          "service",
          "New service has been created successfully"
        );
      }
    },
  });
  // Table-inline updates (e.g. active/archive toggle) — uses fire-and-forget mutate.
  const { mutate: mutatePatchService } = useMutation({
    mutationFn: patchService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"], exact: true });
      toast.success("Service Updated", "Service has been updated successfully");
    },
    onError: (err) => {
      const errorMessage =
        err.response?.data?.error?.message ||
        "An error occurred while updating the service";
      toast.error("Operation Failed", errorMessage);
    },
  });

  // Form-driven updates — awaited so ServiceForm can handle errors inline.
  const { mutateAsync: mutatePatchServiceAsync } = useMutation({
    mutationFn: patchService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"], exact: true });
      toast.success("Service Updated", "Service has been updated successfully");
      closeDrawerHandler();
    },
  });
  const { mutate: mutateDeleteService } = useMutation({
      mutationFn: deleteService,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["services"], exact: true });
        queryClient.invalidateQueries({ queryKey: ["setupProgress"], exact: true });
        toast.success("Service Removed", "The service has been removed");
      },
      onError: (err) => {
        const errorMessage =
          err.response?.data?.error?.message ||
          "An error occurred while deleting the service";
        toast.error("Operation Failed", errorMessage);
        console.error("Error deleting service:", err);
      },
    });

  const closeDrawerHandler = () => {
    setShowDrawer(false);
    setDefaultService(null);
    setIsFormDirty(false);
    setServiceModalTitle(null);
  };
  const deleteServiceHandler = (id) => {
    const selectedService = services.find((service) => service._id === id);

    // If service is not found, don't proceed with deletion
    if (!selectedService) {
      console.error("Service not found for deletion:", id);
      return;
    }

    setDeleteModal({
      modalTitle: "Delete service",
      modalMessage: (
        <>
          Are you sure you want to delete the{" "}
          <strong>{selectedService.name}</strong> service?
        </>
      ),
      primaryCaption: "Delete",
      secondaryCaption: "Cancel",
      primaryClickHandler: () => {
        mutateDeleteService(id);
        setShowDeleteModal(false);
        setDeleteModal(null);
      },
      dismissModalHandler: () => {
        setShowDeleteModal(false);
        setDeleteModal(null);
      },
    });
    setShowDeleteModal(true);
  };
  const editServiceHandler = (id) => {
    const selectedService = services.find((service) => service._id === id);

    // If service is not found, don't proceed with editing
    if (!selectedService) {
      console.error("Service not found for editing:", id);
      return;
    }

    setDefaultService(selectedService);
    setShowDrawer(true);
  };
  useEffect(() => {
    if (isLoadingGetServices) return;
    const hasServices = services && services.length > 0;
    setShowEmptyState(!hasServices);
  }, [services, isLoadingGetServices]);

  if (isLoadingGetServices) {
    return <ServicePageSkeleton />;
  }

  return (
    <>
      {showDeleteModal && deleteModal ? (
        <AlertModal isOpen={showDeleteModal} {...deleteModal} />
      ) : null}
      {!showEmptyState ? (
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center mb-6">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-ink-900">
                Services
              </h1>
              <p className="mt-2 text-sm text-ink-700">
                Manage your services.
              </p>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
              <PrimaryButton onClick={() => setShowDrawer(true)} colour="blue" className="inline-flex items-center">
                <PlusIcon aria-hidden="true" className="-ml-0.5 mr-1.5 size-5" />
                Add Service
              </PrimaryButton>
            </div>
          </div>

          {unbookable.length > 0 && (
            <div className="mb-6">
              <ServiceAttentionStrip
                unbookable={unbookable}
                bookabilityById={bookabilityById}
                providerTypes={providerTypes}
              />
            </div>
          )}

          <ServiceDataTable
              services={services}
              patchService={mutatePatchService}
              deleteService={deleteServiceHandler}
              editService={editServiceHandler}
              bookabilityById={bookabilityById}
            />
        </div>
      ) : null}
      {showDrawer ? (
        <Modal
          open={showDrawer}
          onClose={closeDrawerHandler}
          title={
            serviceModalTitle ||
            (defaultService ? "Edit Service" : "Create New Service")
          }
          size="md"
          confirmClose={isFormDirty}
          closeRef={modalCloseRef}
          scrollResetKey={serviceModalTitle}
        >
          <ServiceForm
            closeDrawerHandler={closeDrawerHandler}
            saveService={mutateSaveService}
            patchService={mutatePatchServiceAsync}
            defaultService={defaultService}
            mode={defaultService ? FORM_MODE.UPDATE : FORM_MODE.CREATE}
            onCancelHandler={() => modalCloseRef.current?.()}
            setIsFormDirty={setIsFormDirty}
            onTitleChange={setServiceModalTitle}
          />
        </Modal>
      ) : null}
      {showEmptyState ? (
        <EmptyState
          text={"No services"}
          subText={"Get started by creating a new service."}
          buttonText={"New Service"}
          onClick={() => setShowDrawer(true)}
        />
      ) : null}
    </>
  );
};

export default ServicePage;
